import React, { useEffect, useMemo, useState } from "react";
import { AppAPIService } from "../../../../services/AppAPIService";
import { formatCPF } from "../../../../utils/validators/IsValidCpfCnpj";
import styles from "./styles.module.scss";

const stageLabels = {
  OVERDUE_HUMAN_REVIEW: "Vencidos",
  D0_VOICEBOT: "Hoje",
  D3_SMS: "D-3",
  D5_WHATSAPP: "D-5",
  D10_EMAIL: "D-10",
  NO_DEADLINE_REVIEW: "Sem prazo",
};

const channelLabels = {
  supervisao_humana: "Supervisão",
  voicebot: "Voicebot",
  sms: "SMS",
  whatsapp: "WhatsApp",
  email: "E-mail",
};

const documentStatusLabels = {
  pending: "Pendente",
  rejected: "Rejeitado",
  validating: "Em validação",
};

const followupStatusLabels = {
  planned: "Planejado",
  paused: "Pausado",
  resolved: "Tratado",
  sent: "Enviado",
  failed: "Falhou",
};

const auditStatusLabels = {
  attempting: "Preparando envio",
  processed: "Processado",
  delivered: "Entregue",
  opened: "Aberto",
  "machine-opened": "Abertura automática",
  clicked: "Clicou no portal",
  bounced: "Devolvido",
  bounce: "Devolvido",
  blocked: "Bloqueado",
  dropped: "Descartado",
  spamreport: "Marcado como spam",
  unsubscribe: "Descadastrado",
  sent: "Enviado",
  failed: "Falhou",
  deferred: "Adiado",
  simulated: "Simulado",
};

const auditEventLabels = {
  "provider-request": "Solicitação preparada",
  "provider-accepted": "Aceito pelo provedor",
  "provider-rejected": "Recusado pelo provedor",
  "provider-processed": "E-mail processado",
  "provider-delivered": "E-mail entregue",
  "provider-open": "Abertura detectada",
  "provider-click": "Clique detectado",
  "provider-deferred": "Entrega adiada",
  "provider-bounce": "E-mail devolvido",
  "provider-blocked": "E-mail bloqueado",
  "provider-dropped": "E-mail descartado",
  "provider-spamreport": "Marcado como spam",
  "provider-unsubscribe": "Descadastro registrado",
};

const formatDate = (value) => {
  if (!value) return "-";
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-BR");
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR");
};

const formatChannels = (channels = []) =>
  channels.map((channel) => channelLabels[channel] || channel).join(", ");

const isPositiveEmailEngagement = (event) =>
  event?.channel === "email" &&
  (["opened", "clicked"].includes(event?.status) ||
    ["provider-open", "provider-click"].includes(event?.eventType));

const resolveRequestMessage = (error, fallback) => {
  const status = error?.response?.status;
  const responseMessage = error?.response?.data?.message;
  const detail = Array.isArray(responseMessage)
    ? responseMessage.join("; ")
    : responseMessage;

  if (status === 400) {
    return `${fallback} A API recusou os filtros informados${
      detail ? `: ${detail}` : "."
    }`;
  }

  if (status === 401) {
    return "Sua sessão local não foi aceita pela API. Saia e entre novamente neste endereço local para renovar o acesso.";
  }

  if (status === 403) {
    return "Seu usuário não tem permissão para acessar Pendências Documentais neste ambiente.";
  }

  if (status >= 500) {
    return "A API não conseguiu processar Pendências Documentais agora. Tente novamente em instantes.";
  }

  return error?.message === "Network Error"
    ? "Não foi possível conectar à API de Pendências Documentais. Verifique o ambiente e tente novamente."
    : fallback;
};

const DocumentCadencePage = () => {
  window.setPageTitle("Pendências Documentais");

  const api = useMemo(() => AppAPIService.getInstance(), []);
  const [daysAhead, setDaysAhead] = useState(10);
  const [daysPast, setDaysPast] = useState(5);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("all");
  const [channel, setChannel] = useState("all");
  const [documentStatus, setDocumentStatus] = useState("all");
  const [followupStatus, setFollowupStatus] = useState("all");
  const [auditStatus, setAuditStatus] = useState("all");
  const [historyProcess, setHistoryProcess] = useState("");
  const [historyClient, setHistoryClient] = useState("");
  const [historyDocument, setHistoryDocument] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [followups, setFollowups] = useState(null);
  const [dispatchHistory, setDispatchHistory] = useState(null);
  const [viewMode, setViewMode] = useState("simulation");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showOperationalActions, setShowOperationalActions] = useState(false);
  const [expandedHistoryProcesses, setExpandedHistoryProcesses] = useState({});

  const buildParams = (nextPage = page) => {
    const params = new URLSearchParams({
      daysAhead: String(daysAhead),
      daysPast: String(daysPast),
      page: String(nextPage),
      pageSize: String(pageSize),
    });

    if (search.trim()) {
      params.set("search", search.trim());
    }

    if (stage !== "all") params.set("stage", stage);
    if (channel !== "all") params.set("channel", channel);
    if (documentStatus !== "all") params.set("documentStatus", documentStatus);
    if (followupStatus !== "all") params.set("followupStatus", followupStatus);
    if (overdueOnly) params.set("overdueOnly", "true");

    return params;
  };

  const fetchSimulation = async (nextPage = page) => {
    setLoading(true);
    setMessage("");

    try {
      const data = await api.makeHttpRequest({
        method: "GET",
        url: `/document-cadence/simulate?${buildParams(nextPage).toString()}`,
      });
      setSimulation(data);
      setPage(nextPage);
      setViewMode("simulation");
    } catch (error) {
      setMessage(
        resolveRequestMessage(
          error,
          "Não foi possível carregar os documentos pendentes agora.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowups = async (nextPage = page) => {
    setLoading(true);
    setMessage("");

    try {
      const data = await api.makeHttpRequest({
        method: "GET",
        url: `/document-cadence/followups?${buildParams(nextPage).toString()}`,
      });
      setFollowups(data);
      setPage(nextPage);
      if (data?.summary?.migrationRequired) {
        setMessage(
          "Fila operacional ainda aguardando preparação neste ambiente. A simulação continua disponível.",
        );
      }
      setViewMode("followups");
    } catch (error) {
      setMessage(
        resolveRequestMessage(
          error,
          "Não foi possível carregar a fila de documentos pendentes agora.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchDispatchHistory = async (nextPage = page) => {
    setLoading(true);
    setMessage("");
    const params = new URLSearchParams({
      page: String(nextPage),
      pageSize: String(pageSize),
    });
    if (search.trim()) params.set("search", search.trim());
    if (historyProcess.trim())
      params.set("processNumber", historyProcess.trim());
    if (historyClient.trim()) params.set("clientName", historyClient.trim());
    if (historyDocument.trim()) params.set("document", historyDocument.trim());
    if (historyDateFrom) params.set("dateFrom", historyDateFrom);
    if (historyDateTo) params.set("dateTo", historyDateTo);
    if (channel !== "all") params.set("channel", channel);
    if (auditStatus !== "all") params.set("status", auditStatus);

    try {
      const data = await api.makeHttpRequest({
        method: "GET",
        url: `/document-cadence/dispatch-history?${params.toString()}`,
      });
      setDispatchHistory(data);
      setPage(nextPage);
      setViewMode("history");
      if (data?.summary?.migrationRequired) {
        setMessage(
          "Histórico definitivo ainda aguardando preparação neste ambiente. Nenhum envio real deve ser liberado.",
        );
      }
    } catch (error) {
      setMessage(
        resolveRequestMessage(
          error,
          "Não foi possível carregar o histórico de envios agora.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const syncFollowups = async () => {
    setLoading(true);
    setMessage("");

    try {
      const data = await api.makeHttpRequest({
        method: "POST",
        url: `/document-cadence/sync?${buildParams().toString()}`,
      });
      setMessage(
        data.message ||
          `Fila sincronizada em modo seguro: ${data.created || 0} novo(s), ${
            data.updated || 0
          } atualizado(s). Nenhum envio real foi executado.`,
      );
      await fetchFollowups(1);
    } catch (error) {
      setMessage(
        resolveRequestMessage(
          error,
          "Não foi possível sincronizar a fila de documentos pendentes agora.",
        ),
      );
      setLoading(false);
    }
  };

  const updateFollowup = async (id, action, defaultNote) => {
    const note = window.prompt("Observação para histórico:", defaultNote || "");
    if (note === null) return;

    setLoading(true);
    setMessage("");

    try {
      await api.makeHttpRequest({
        method: "PATCH",
        url: `/document-cadence/followups/${id}/${action}`,
        data: { note },
      });
      await fetchFollowups(page);
    } catch (error) {
      setMessage(
        resolveRequestMessage(
          error,
          "Não foi possível atualizar este item da fila.",
        ),
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSimulation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFollowupMode = viewMode === "followups";
  const isHistoryMode = viewMode === "history";
  const summary = isHistoryMode
    ? dispatchHistory?.summary
    : isFollowupMode
      ? followups?.summary
      : simulation?.summary;
  const items = Array.isArray(simulation?.items) ? simulation.items : [];
  const followupItems = Array.isArray(followups?.items) ? followups.items : [];
  const historyItems = Array.isArray(dispatchHistory?.items)
    ? dispatchHistory.items
    : [];
  const visibleItemsCount = isHistoryMode
    ? historyItems.length
    : isFollowupMode
      ? followupItems.length
      : items.length;
  const stages = summary?.byStage || {};
  const pagination = summary?.pagination;

  const applyFilters = () => {
    if (isHistoryMode) {
      fetchDispatchHistory(1);
      return;
    }
    if (isFollowupMode) {
      fetchFollowups(1);
      return;
    }

    fetchSimulation(1);
  };

  const goToPage = (nextPage) => {
    if (loading || nextPage < 1) return;

    if (isHistoryMode) {
      fetchDispatchHistory(nextPage);
      return;
    }

    if (isFollowupMode) {
      fetchFollowups(nextPage);
      return;
    }

    fetchSimulation(nextPage);
  };

  const toggleHistoryProcess = (lawsuitId) => {
    setExpandedHistoryProcesses((current) => ({
      ...current,
      [lawsuitId]: !current[lawsuitId],
    }));
  };

  return (
    <div className={styles.container}>
      <section className={styles.header}>
        <h1>Pendências Documentais</h1>
        <p>Acompanhamento automático de documentos por prazo e canal.</p>
      </section>

      <section className={styles.toolbar}>
        <div className={styles.toolbarFields}>
          {isHistoryMode ? (
            <>
              <label className={styles.historyFilterField}>
                Número do processo
                <input
                  type="search"
                  value={historyProcess}
                  placeholder="Digite o número do processo"
                  onChange={(event) => setHistoryProcess(event.target.value)}
                />
              </label>
              <label className={styles.historyFilterField}>
                Nome do cliente
                <input
                  type="search"
                  value={historyClient}
                  placeholder="Digite o nome do cliente"
                  onChange={(event) => setHistoryClient(event.target.value)}
                />
              </label>
              <label className={styles.historyFilterField}>
                Documento
                <input
                  type="search"
                  value={historyDocument}
                  placeholder="Nome ou número do documento"
                  onChange={(event) => setHistoryDocument(event.target.value)}
                />
              </label>
              <label>
                Data inicial
                <input
                  type="date"
                  value={historyDateFrom}
                  onChange={(event) => setHistoryDateFrom(event.target.value)}
                />
              </label>
              <label>
                Data final
                <input
                  type="date"
                  value={historyDateTo}
                  onChange={(event) => setHistoryDateTo(event.target.value)}
                />
              </label>
            </>
          ) : (
            <label className={styles.searchField}>
              Buscar
              <input
                type="search"
                value={search}
                placeholder="Processo, cliente, CPF/CNPJ, parte ou documento"
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applyFilters();
                }}
              />
            </label>
          )}
          {!isHistoryMode && (
            <>
              <label>
                Dias futuros (máx. 10)
                <input
                  min="1"
                  max="10"
                  type="number"
                  value={daysAhead}
                  onChange={(event) => setDaysAhead(event.target.value)}
                />
              </label>
              <label>
                Vencidos recentes
                <input
                  min="0"
                  max="30"
                  type="number"
                  value={daysPast}
                  onChange={(event) => setDaysPast(event.target.value)}
                />
              </label>
            </>
          )}
          <label>
            Itens por página
            <input
              min="1"
              max="200"
              type="number"
              value={pageSize}
              onChange={(event) => setPageSize(event.target.value)}
            />
          </label>
          {!isHistoryMode && (
            <label>
              Etapa
              <select
                value={stage}
                onChange={(event) => setStage(event.target.value)}
              >
                <option value="all">Todas</option>
                {Object.keys(stageLabels).map((value) => (
                  <option key={value} value={value}>
                    {stageLabels[value]}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Canal
            <select
              value={channel}
              onChange={(event) => setChannel(event.target.value)}
            >
              <option value="all">Todos</option>
              {Object.keys(channelLabels).map((value) => (
                <option key={value} value={value}>
                  {channelLabels[value]}
                </option>
              ))}
            </select>
          </label>
          {!isHistoryMode && (
            <label>
              Status doc.
              <select
                value={documentStatus}
                onChange={(event) => setDocumentStatus(event.target.value)}
              >
                <option value="all">Todos</option>
                {Object.keys(documentStatusLabels).map((value) => (
                  <option key={value} value={value}>
                    {documentStatusLabels[value]}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            {isHistoryMode ? "Status envio" : "Status fila"}
            <select
              value={isHistoryMode ? auditStatus : followupStatus}
              onChange={(event) =>
                isHistoryMode
                  ? setAuditStatus(event.target.value)
                  : setFollowupStatus(event.target.value)
              }
            >
              <option value="all">Todos</option>
              {Object.keys(
                isHistoryMode ? auditStatusLabels : followupStatusLabels,
              ).map((value) => (
                <option key={value} value={value}>
                  {
                    (isHistoryMode ? auditStatusLabels : followupStatusLabels)[
                      value
                    ]
                  }
                </option>
              ))}
            </select>
          </label>
          {!isHistoryMode && (
            <button
              type="button"
              className={
                overdueOnly ? styles.toggleButtonActive : styles.toggleButton
              }
              onClick={() => setOverdueOnly((currentValue) => !currentValue)}
            >
              Somente vencidos
            </button>
          )}
        </div>
        <div className={styles.toolbarActions}>
          <button type="button" onClick={applyFilters} disabled={loading}>
            Buscar
          </button>
          <button
            type="button"
            onClick={() => fetchSimulation(1)}
            disabled={loading}
          >
            {loading ? "Atualizando..." : "Atualizar painel"}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => fetchDispatchHistory(1)}
            disabled={loading}
          >
            Histórico de envios
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setShowOperationalActions((current) => !current)}
          >
            Ações operacionais
          </button>
        </div>
        {showOperationalActions && (
          <div className={styles.operationalActions}>
            <button type="button" onClick={syncFollowups} disabled={loading}>
              Preparar avisos
            </button>
            <button
              type="button"
              onClick={() => fetchFollowups(1)}
              disabled={loading}
            >
              Ver avisos preparados
            </button>
          </div>
        )}
      </section>

      {message && <section className={styles.notice}>{message}</section>}

      <section className={styles.summary}>
        <div>
          <span>Registros na página</span>
          <strong>{summary?.itemsOnPage ?? visibleItemsCount ?? 0}</strong>
        </div>
        <div>
          <span>
            {isHistoryMode ? "Processos encontrados" : "Documentos pendentes"}
          </span>
          <strong>
            {isHistoryMode
              ? summary?.totalProcesses || 0
              : summary?.documentsTotal ||
                summary?.documentsEvaluated ||
                summary?.total ||
                0}
          </strong>
        </div>
        <div>
          <span>{isHistoryMode ? "Comunicações" : "Modo"}</span>
          <strong>
            {isHistoryMode
              ? summary?.totalCommunications || 0
              : isFollowupMode
                ? "Avisos"
                : "Acompanhamento"}
          </strong>
        </div>
        <div>
          <span>{isHistoryMode ? "Ações registradas" : "Data base"}</span>
          <strong>
            {isHistoryMode
              ? summary?.totalEvents || 0
              : formatDate(summary?.referenceDate)}
          </strong>
        </div>
      </section>

      {!isHistoryMode && (
        <section className={styles.stageGrid}>
          {Object.keys(stageLabels).map((stage) => (
            <article
              key={stage}
              title={
                stage === "D3_SMS"
                  ? "Prazos entre 1 e 3 dias"
                  : stage === "D5_WHATSAPP"
                    ? "Prazos entre 4 e 5 dias"
                    : stage === "D10_EMAIL"
                      ? "Prazos entre 6 e 10 dias"
                      : undefined
              }
            >
              <span>{stageLabels[stage]}</span>
              <strong>{stages[stage]?.lawsuits || 0}</strong>
              <small>{stages[stage]?.documents || 0} documento(s)</small>
            </article>
          ))}
        </section>
      )}

      <section className={styles.listHeader}>
        <div>
          <h2>
            {isHistoryMode
              ? "Histórico permanente de envios"
              : isFollowupMode
                ? "Fila sincronizada"
                : "Processos com documentos pendentes"}
          </h2>
          <p>
            {isHistoryMode
              ? "Um histórico por processo, reunindo e-mails, WhatsApps e seus resultados."
              : isFollowupMode
                ? "Avisos preparados para acompanhamento, pausa ou tratamento manual."
                : "Processos que exigem acompanhamento de documentos."}
          </p>
        </div>
        {!isHistoryMode && (
          <span>
            Ambiente seguro. Destinatário de teste:{" "}
            {summary?.emailSafety?.allowedRecipient || "fabiomag@gmail.com"}
          </span>
        )}
      </section>

      {pagination && (
        <section className={styles.pagination}>
          <span>
            Mostrando {pagination.showingFrom || 0} a{" "}
            {pagination.showingTo || 0} de {pagination.totalItems || 0}{" "}
            registro(s)
          </span>
          <div>
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={loading || !pagination.hasPreviousPage}
            >
              Anterior
            </button>
            <strong>
              Página {pagination.page || page} de {pagination.totalPages || 1}
            </strong>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={loading || !pagination.hasNextPage}
            >
              Próxima
            </button>
          </div>
        </section>
      )}

      <section className={styles.cadenceList}>
        {loading && (
          <div className={styles.emptyState}>
            Carregando documentos pendentes...
          </div>
        )}
        {!loading &&
          !isHistoryMode &&
          !isFollowupMode &&
          items.length === 0 && (
            <div className={styles.emptyState}>
              Nenhum processo encontrado na janela selecionada.
            </div>
          )}
        {!loading &&
          !isHistoryMode &&
          isFollowupMode &&
          followupItems.length === 0 && (
            <div className={styles.emptyState}>
              A fila ainda não foi sincronizada para a janela selecionada.
            </div>
          )}
        {!loading && isHistoryMode && historyItems.length === 0 && (
          <div className={styles.emptyState}>
            Nenhum processo encontrado no histórico de envios.
          </div>
        )}

        {!loading &&
          isHistoryMode &&
          historyItems.map((item) => {
            const isExpanded = Boolean(
              expandedHistoryProcesses[item.lawsuitId],
            );
            const visibleEvents = isExpanded
              ? item.events || []
              : (item.events || []).slice(0, 6);
            return (
              <article
                className={styles.historyProcessCard}
                key={item.lawsuitId}
              >
                <div className={styles.historyProcessHeader}>
                  <div>
                    <span className={styles.historyEyebrow}>Processo</span>
                    <strong>
                      {item.courtCaseNumber || "Número não informado"}
                    </strong>
                    <span>{item.clientName || "Cliente não informado"}</span>
                    {item.clientDocument && (
                      <span>
                        {item.clientDocumentType ||
                          (String(item.clientDocument).replace(/\D/g, "")
                            .length === 14
                            ? "CNPJ"
                            : "CPF")}
                        : {formatCPF(String(item.clientDocument))}
                      </span>
                    )}
                  </div>
                  <div className={styles.historyProcessSummary}>
                    <span>
                      {item.communicationsCount || 0} comunicação(ões)
                    </span>
                    <span>
                      {item.eventsCount || item.events?.length || 0} ação(ões)
                    </span>
                    <small>
                      Última ação: {formatDateTime(item.lastActivityAt)}
                    </small>
                  </div>
                </div>

                <div className={styles.historyOverview}>
                  <div>
                    <strong>Documentos relacionados</strong>
                    {item.documents?.length ? (
                      <ul>
                        {item.documents.map((document) => (
                          <li key={document.name}>{document.name}</li>
                        ))}
                      </ul>
                    ) : (
                      <span>Nenhum documento identificado</span>
                    )}
                  </div>
                  <div className={styles.channelsSection}>
                    <strong>Canais utilizados</strong>
                    <div className={styles.channelBadges}>
                      {(item.channels || []).map((itemChannel) => (
                        <span key={itemChannel} className={styles.stageBadge}>
                          {channelLabels[itemChannel] || itemChannel}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={styles.historyTimeline}>
                  {visibleEvents.map((event, eventIndex) => {
                    const isPositiveEmail = isPositiveEmailEngagement(event);

                    return (
                      <div
                        className={styles.historyTimelineItem}
                        key={event.eventId || eventIndex}
                      >
                      <span
                        className={styles.timelineMarker}
                        aria-hidden="true"
                      />
                      <div className={styles.timelineContent}>
                        <div className={styles.timelineTitle}>
                          <div>
                            <strong>
                              {auditEventLabels[event.eventType] ||
                                event.eventType}
                            </strong>
                            <span>{formatDateTime(event.occurredAt)}</span>
                          </div>
                          <div
                            className={`${styles.channelBadges} ${styles.timelineBadges}`}
                          >
                            <span
                              className={`${styles.stageBadge} ${
                                isPositiveEmail ? styles.successBadge : ""
                              }`}
                            >
                              {channelLabels[event.channel] || event.channel}
                            </span>
                            <span
                              className={`${styles.statusBadge} ${
                                isPositiveEmail ? styles.successBadge : ""
                              }`}
                            >
                              {auditStatusLabels[event.status] || event.status}
                            </span>
                          </div>
                        </div>
                        <div className={styles.timelineDetails}>
                          {event.document && (
                            <span>
                              <strong>Documento:</strong> {event.document.name}
                            </span>
                          )}
                          {event.recipientMasked && (
                            <span>
                              <strong>Destino:</strong> {event.recipientMasked}
                            </span>
                          )}
                          {event.deadline && (
                            <span>
                              <strong>Prazo:</strong>{" "}
                              {formatDate(event.deadline)}
                            </span>
                          )}
                        </div>
                        {event.errorMessage && (
                          <div className={styles.timelineError}>
                            {event.errorMessage}
                          </div>
                        )}
                      </div>
                      </div>
                    );
                  })}
                </div>
                {(item.events || []).length > 6 && (
                  <button
                    type="button"
                    className={styles.timelineToggle}
                    onClick={() => toggleHistoryProcess(item.lawsuitId)}
                  >
                    {isExpanded
                      ? "Mostrar somente as ações mais recentes"
                      : `Ver todas as ${item.events.length} ações`}
                  </button>
                )}
              </article>
            );
          })}

        {!loading &&
          !isHistoryMode &&
          isFollowupMode &&
          followupItems.map((item) => (
            <article className={styles.cadenceCard} key={item.id}>
              <div className={styles.cardHeader}>
                <div>
                  <strong>
                    {item.lawsuit?.courtCaseNumber ||
                      `Processo ${item.lawsuit?.id || "-"}`}
                  </strong>
                  <span>
                    {item.lawsuit?.clientName || "Cliente não informado"}
                  </span>
                </div>
                <div className={styles.badges}>
                  <span className={styles.stageBadge}>
                    {stageLabels[item.stage] || item.stage}
                  </span>
                  <span className={styles.statusBadge}>
                    {followupStatusLabels[item.status] ||
                      item.status ||
                      "Não informado"}
                  </span>
                </div>
              </div>

              <div className={styles.cardDetails}>
                <span>
                  <strong>Documento:</strong>{" "}
                  {item.document?.name || "Documento"}
                </span>
                <span>
                  <strong>Status documento:</strong>{" "}
                  {documentStatusLabels[item.document?.status] ||
                    item.document?.status ||
                    "Não informado"}
                </span>
                <span>
                  <strong>Canal:</strong>{" "}
                  {channelLabels[item.channel] || item.channel}
                </span>
                <span>
                  <strong>Data limite:</strong> {formatDate(item.deadline)}
                </span>
                <span>
                  <strong>E-mail real:</strong>{" "}
                  {item.originalRecipient || "Não informado"}
                </span>
                <span>
                  <strong>E-mail de teste:</strong>{" "}
                  {item.safeRecipient || "fabiomag@gmail.com"}
                </span>
              </div>

              {item.note && (
                <div className={styles.followupNote}>{item.note}</div>
              )}

              <div className={styles.actions}>
                {item.status !== "paused" && item.status !== "resolved" && (
                  <button
                    type="button"
                    onClick={() =>
                      updateFollowup(item.id, "pause", "Pausado manualmente.")
                    }
                  >
                    Pausar
                  </button>
                )}
                {item.status === "paused" && (
                  <button
                    type="button"
                    onClick={() =>
                      updateFollowup(item.id, "resume", "Retomado manualmente.")
                    }
                  >
                    Retomar
                  </button>
                )}
                {item.status !== "resolved" && (
                  <button
                    type="button"
                    onClick={() =>
                      updateFollowup(item.id, "resolve", "Tratado manualmente.")
                    }
                  >
                    Marcar como tratado
                  </button>
                )}
              </div>
            </article>
          ))}

        {!loading &&
          !isHistoryMode &&
          !isFollowupMode &&
          items.map((item) => (
            <article
              className={styles.cadenceCard}
              key={
                item.lawsuitId ||
                item.documentId ||
                item.courtCaseNumber ||
                item.clientDocument
              }
            >
              <div className={styles.cardHeader}>
                <div>
                  <strong>
                    {item.courtCaseNumber ||
                      (item.lawsuitId
                        ? `Processo ${item.lawsuitId}`
                        : "Documento sem processo vinculado")}
                  </strong>
                  <span>{item.clientName || "Cliente não informado"}</span>
                </div>
                <span className={styles.stageBadge}>
                  {stageLabels[item.stage] || item.stage}
                </span>
              </div>

              <div className={styles.cardDetails}>
                <span>
                  <strong>Parte adversa:</strong>{" "}
                  {item.adverseParty || "Não informada"}
                </span>
                <span>
                  <strong>Data limite:</strong> {formatDate(item.limitDate)}
                </span>
                <span>
                  <strong>Dias:</strong> {item.daysUntilLimit}
                </span>
                <span>
                  <strong>Canais previstos:</strong>{" "}
                  {formatChannels(item.channelPlan)}
                </span>
                <span>
                  <strong>E-mail de teste:</strong>{" "}
                  {item.emailPlan?.safeRecipient || "fabiomag@gmail.com"}
                </span>
              </div>

              <div className={styles.documents}>
                <strong>Documentos pendentes</strong>
                <ul>
                  {item.pendingDocuments.map((document) => (
                    <li key={document.documentId}>
                      {document.name}{" "}
                      <span>
                        {documentStatusLabels[document.status] ||
                          document.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
      </section>
    </div>
  );
};

export default DocumentCadencePage;
